import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseurl } from "../../helper/Baseurl";
import { useTranslation } from "../../context/TranslationContext";
import {
  FaSpinner,
  FaUsers,
  FaChalkboardTeacher,
  FaPhone,
  FaCity,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import SearchAndSortBar from "../../component/SearchAndSortBar";
import CenterCard from "../../component/Center/CenterCard";
const customIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
  iconSize: [32, 32],
});

export default function Centers() {
  const { translations, language } = useTranslation();
  const isArabic = language === "ar";

  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCity, setSearchCity] = useState("");
  const [searchAddress, setSearchAddress] = useState("");
  const [sortOption, setSortOption] = useState("فرز حسب");
  const [openCenters, setOpenCenters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const defaultPosition = [32.8872, 13.1913];

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const { data } = await axios.get(baseurl + "public/center");
        setCenters(data);
      } catch (err) {
        console.error("Error fetching centers:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCenters();
  }, []);

  const handleSearchChange = (e) => setSearchAddress(e.target.value);
  const handleSortChange = (e) => setSortOption(e.target.value);
  const toggleCenter = (id) => {
    setOpenCenters((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const handlePageChange = (page) => setCurrentPage(page);

  const filteredCenters = centers.filter(
    (center) =>
      center.city.includes(searchCity) &&
      center.address.includes(searchAddress)
  );

  const totalPages = Math.ceil(filteredCenters.length / itemsPerPage);
  const paginatedCenters = filteredCenters.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <FaSpinner className="text-4xl animate-spin" />
      </div>
    );
  }

  return (
    <div className={`flex pt-24 w-full ${isArabic ? "flex-col md:flex-row" : "flex-col-reverse md:flex-row-reverse"}`}>
      <div className="container mx-auto px-4 py-6">
        <div
          className="p-4"
          style={{
            fontFamily: "Tajwal, sans-serif",
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
          }}
        >
          <h1 className="text-xl font-bold mb-1">{translations.pageTitle}</h1>
          <h4 className="text-l font-bold text-gray-500 mr-5">{translations.pageSubtitle}</h4>
          <hr className="my-2 border-gray-200" />

          <SearchAndSortBar
            searchTerm={searchAddress}
            handleSearchChange={handleSearchChange}
            sortOption={sortOption}
            handleSortChange={handleSortChange}
            translations={translations}
          />

          <MapContainer
            center={defaultPosition}
            zoom={10}
            style={{ height: "500px", width: "100%", marginTop: "10px", position: "relative", zIndex: 0 }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {centers.map((center, index) => (
              <Marker key={index} position={[center.lat, center.lng]} icon={customIcon}>
                <Popup>
                  <div className="p-2 text-gray-800">
                    <h2 className={`text-lg font-bold ${center.isActive ? "text-green-600" : "text-red-600"}`}>
                      {center.name}
                    </h2>
                    <p className="text-sm flex items-center">
                      <FaCity className="text-gray-500 ml-1" /> {center.city}
                    </p>
                    <p className="text-sm flex items-center">
                      <FaPhone className="text-gray-500 ml-1" /> {center.phoneNumber}
                    </p>
                    <p className="text-sm flex items-center">
                      <FaUsers className="text-gray-500 ml-1" /> {center.numberOfStudents} طالب
                    </p>
                    <p className="text-sm flex items-center">
                      <FaChalkboardTeacher className="text-gray-500 ml-1" /> {center.numberOfTeachers} معلم
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-5">
          {paginatedCenters.map((center) => (
            <CenterCard
              key={center.id}
              center={center}
              isOpen={openCenters[center.id]}
              toggleCenter={toggleCenter}
              isArabic={isArabic}
            />
          ))}
        </div>
          <div className="mt-4">
            <ul className="flex justify-center items-center space-x-2">
              <li>
                <button
                  className="px-3 py-1 rounded-full text-primary-blue"
                  onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {isArabic ? <FaArrowRight /> : <FaArrowLeft />}
                </button>
              </li>
              {Array.from({ length: totalPages }, (_, index) => (
                <li key={index}>
                  <button
                    className={`px-3 py-1 rounded-full ${
                      currentPage === index + 1 ? "bg-primary-blue text-white" : "text-gray-700"
                    }`}
                    onClick={() => handlePageChange(index + 1)}
                  >
                    {index + 1}
                  </button>
                </li>
              ))}
              <li>
                <button
                  className="px-3 py-1 rounded-full text-primary-blue"
                  onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  {isArabic ? <FaArrowLeft /> : <FaArrowRight />}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
